import { useState } from "react";

import { toast } from "react-toastify";
import { DocumentItem } from "../../../../types/documents";
import apiService from "../../../../service/Api";

export interface UploadedDocumentResponse {
  id: string;
  name: string;
  description: string;
  category: string;
  document_field: string;
}

interface UseDocumentsReturn {
  documentLoading: boolean;
  handleViewDocument: (documentField: string) => void;
  handleSaveDocument: (
    bookingId: string,
    name: string,
    category: string,
    description: string,
    file: File
  ) => Promise<DocumentItem | null>;
  handleDocumentDelete: (documentId: string) => Promise<boolean>;
}

export const useDocuments = (): UseDocumentsReturn => {
  const [documentLoading, setDocumentLoading] = useState(false);

  const handleViewDocument = (document_field: string) => {
    if (document_field) {
      window.open(document_field, "_blank");
    } else {
      console.warn("Document URL is not available.");
    }
  };

  const handleSaveDocument = async (
    bookingId: string,
    name: string,
    category: string,
    description: string,
    file: File
  ): Promise<DocumentItem | null> => {
    if (!file || !name || !bookingId || !description) {
      toast.error(
        "Missing document, name, or booking ID. Please provide all details.",
        {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        }
      );
      return null;
    }

    setDocumentLoading(true);
    try {
      const formData = new FormData();
      formData.append("booking_id", bookingId);
      formData.append("name", name);
      formData.append("category", category);
      formData.append("description", description);
      formData.append("document_field", file);

      const response = await apiService.post(
        "/api/bookings/documents/",
        formData
      );

      const uploadedDoc = response.data as UploadedDocumentResponse;

      toast.success("Document uploaded successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });

      return {
        id: uploadedDoc.id,
        name: uploadedDoc.name,
        description: uploadedDoc.description,
        document_field: uploadedDoc.document_field,
      };
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return null;
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleDocumentDelete = async (documentId: string): Promise<boolean> => {
    try {
      await apiService.delete(`/api/bookings/documents/${documentId}/`);

      toast.success("Document deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      return true;
    } catch (err: unknown) {
      console.error("Delete document error:", err);
      let errorMessage = "Failed to delete document. Please try again.";
      const errorResponse = (err as {
        response?: { data?: { detail?: string } | string };
      })?.response;
      if (errorResponse?.data && typeof errorResponse.data === "object") {
        errorMessage = errorResponse.data.detail || errorMessage;
      } else if (typeof errorResponse?.data === "string") {
        errorMessage = errorResponse.data;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      return false;
    }
  };

  return {
    documentLoading,
    handleViewDocument,
    handleSaveDocument,
    handleDocumentDelete,
  };
};
